import { useState, useCallback, useRef } from 'react'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'

/** Whether a file matches an `accept` list the way the file input reads it:
 *  extensions (".pdf"), MIME types ("application/pdf") and wildcards ("image/*"). */
export function matchesAccept(file: File, accept: string | undefined): boolean {
  const rules = (accept ?? '').split(',').map((a) => a.trim().toLowerCase()).filter(Boolean)
  if (rules.length === 0) return true
  const name = file.name.toLowerCase()
  const type = file.type.toLowerCase()
  return rules.some((a) =>
    a.startsWith('.') ? name.endsWith(a) : a.endsWith('/*') ? type.startsWith(a.slice(0, -1)) : type === a)
}

/** A line for `onReject`: "notes.doc isn't a supported file type." */
export function rejectedMessage(files: File[]): string {
  return files.length === 1
    ? `${files[0].name} isn't a supported file type.`
    : `${files.length} files aren't a supported file type: ${files.map((f) => f.name).join(', ')}.`
}

interface FileDropzoneProps {
  /** The files matching `accept`; never called with none. */
  onFilesSelected: (files: File[]) => void
  /** The files that don't match `accept`, which a drop doesn't filter by itself. */
  onReject?: (files: File[]) => void
  accept?: string
  multiple?: boolean
  label?: string
  /** Small line under the label, e.g. accepted types and size. */
  hint?: string
  disabled?: boolean
  /** Replaces the default box styling. The drag state is on
   *  `data-drag-active`, for `data-[drag-active=true]:` variants. */
  className?: string
  /** Replaces the default icon, label and hint. */
  children?: React.ReactNode
}

export default function FileDropzone({
  onFilesSelected,
  onReject,
  accept = '.pdf,.docx,.doc,.txt,.md',
  multiple = true,
  label = 'Drop files here or click to browse',
  hint = 'PDF, DOCX, TXT, Markdown',
  disabled = false,
  className,
  children,
}: FileDropzoneProps) {
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [disabled])

  // The input's `accept` only steers the file picker; a drop brings anything,
  // so both paths filter here.
  const select = useCallback((files: File[]) => {
    const ok = files.filter((f) => matchesAccept(f, accept))
    const rejected = files.filter((f) => !matchesAccept(f, accept))
    if (rejected.length > 0) onReject?.(rejected)
    if (ok.length > 0) onFilesSelected(ok)
  }, [accept, onFilesSelected, onReject])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (disabled) return
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      select(Array.from(e.dataTransfer.files))
    }
  }, [disabled, select])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      select(Array.from(e.target.files))
    }
    if (inputRef.current) inputRef.current.value = ''
  }, [select])

  return (
    <div
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      data-drag-active={dragActive}
      className={className ?? `border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
        dragActive
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-primary/5'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children ?? (
        <>
          <ArrowUpTrayIcon className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xs text-muted-foreground/60 mt-1">{hint}</p>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
      />
    </div>
  )
}
